import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useParams, useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import {
  fetchSubmissionDetails,
  searchSubmissionById,
  handleVote,
  countVotes,
  checkUserVote,
  fetchComments,
  addComment,
} from "../../services/submissionService";
import { initiatePurchase } from "../../services/purchaseService";
import { MIN_CONTRIBUTION, derivePurchase } from "../../utils/purchaseAmount";
import PurchaseSection from "../molecules/SubmissionDetails/PurchaseSection";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
  Backdrop,
} from "@mui/material";
import CommentInput from "../molecules/SubmissionDetails/CommentInput";
import CommentsList from "../molecules/SubmissionDetails/CommentsList";
import LikeDislikeButtons from "../molecules/SubmissionDetails/LikeDislikeButtons";
import SubmissionTitleSection from "../molecules/SubmissionDetails/SubmissionTitleSection";
import SubmissionDetailsSection from "../molecules/SubmissionDetails/SubmissionDetailsSection";
import VideoPlayerSection from "../molecules/SubmissionDetails/VideoPlayerSection";
import ImageDisplaySection from "../molecules/SubmissionDetails/ImageDisplaySection";

const SubmissionDetailPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const { submissionId } = useParams();
  const [submissionDetails, setSubmissionDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [likeStatus, setLikeStatus] = useState(null);
  // The one piece of purchase state. Fee, total, and share count are derived.
  const [amountUsd, setAmountUsd] = useState(0);
  const [sharesAvailable, setSharesAvailable] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [totalShares, setTotalShares] = useState(0);
  const [pricePerShare, setPricePerShare] = useState(0);
  const [remainingShares, setRemainingShares] = useState(0);
  const theme = useTheme();

  const [isProcessing, setIsProcessing] = useState(true);

  const handleAssetStatusChange = (status) => {
    setIsProcessing(status);
  };

  useEffect(() => {
    setUser(currentUser); // Set the user to currentUser from AuthContext
  }, [currentUser]); // Update the user state whenever currentUser changes

  const queryParams = new URLSearchParams(location.search);
  const transactionError = queryParams.has("transaction_error");

  useEffect(() => {
    if (transactionError) {
      setSnackbarMessage("Transaction failed. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [transactionError]);

  // Fetch submission and challenge details using useCallback to stabilize the function
  const getSubmissionAndChallengeDetails = useCallback(async () => {
    try {
      const submissionData = await fetchSubmissionDetails(submissionId);
      setSubmissionDetails(submissionData);

      const submissionSearchResponse = await searchSubmissionById(
        submissionData.submissionSaleId
      );

      const pricePerShare =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares?.pricePerShare;
      const totalShares =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares?.totalShares;
      const purchasedShares =
        submissionSearchResponse[0]?.art?.activeSales[0]?.shares
          ?.purchasedShares;
      const remainingShares = totalShares - purchasedShares;

      setTotalShares(totalShares);
      setPricePerShare(pricePerShare);
      setRemainingShares(remainingShares);

      if (remainingShares > 0) {
        setSharesAvailable(true);
      }

      // Fetch like/dislike counts without requiring authentication
      const { likeCount, dislikeCount } = await countVotes(submissionId);
      setLikeCount(likeCount);
      setDislikeCount(dislikeCount);

      // If the user is authenticated, fetch their vote
      if (user) {
        try {
          const userVote = await checkUserVote(submissionId, user.uid);
          setLikeStatus(userVote);
        } catch (err) {
          console.error("Error checking user vote:", err);
        }
      }

      // Crowdfunding opens at the $5 minimum; buying to own opens at the item's
      // price, which is the only amount that flow ever charges. Guarded because a
      // sale still being processed has no `pricePerShare` yet — the old code called
      // .toFixed() on it and threw into the error state, hiding "Submission Pending".
      if (Number.isFinite(pricePerShare)) {
        setAmountUsd(
          submissionData.challengeData?.allowCrowdFunding
            ? MIN_CONTRIBUTION
            : pricePerShare
        );
      }
    } catch (err) {
      if (err.message.includes("User is not authenticated")) {
        // Set error message only for debugging purposes, do not show to the user
        console.warn(
          "User is not authenticated. Proceeding as unauthenticated."
        );
      } else {
        // setError(`${err.message || err} to see this submission.`);
        setError(
          `This submission is being processed. Please check back soon for updates.`
        );
      }
    } finally {
      setLoading(false);
    }
  }, [submissionId, user]);

  useEffect(() => {
    getSubmissionAndChallengeDetails();
  }, [getSubmissionAndChallengeDetails]);

  useEffect(() => {
    const unsubscribe = fetchComments(
      submissionId,
      setComments,
      setLoadingComments
    );
    return () => unsubscribe();
  }, [submissionId]);

  // Determine if the purchase button should be shown
  const currentUserUid = currentUser?.uid || null; // Safely get current user's UID
  console.log("currentUserUid: ", currentUserUid);

  const submissionOwnerUid = submissionDetails?.userId || null; // Safely get submission owner's UID

  console.log("submissionOwnerUid: ", submissionDetails?.userId);

  // Check if the authenticated user is the submission owner
  const isSubmissionOwner = () => currentUserUid === submissionOwnerUid;

  console.log("isSubmissionOwner: ", isSubmissionOwner());

  // Check if shares are available
  const areSharesAvailable = Boolean(sharesAvailable);

  // Check if the funding goal has been reached

  const isGoalReached =
    submissionDetails?.fundingGoal > 0 && remainingShares <= 0;

  const isSubmissionPending = pricePerShare === undefined;

  const isCrowdFunding = Boolean(
    submissionDetails?.challengeData?.allowCrowdFunding
  );

  // A contribution can never exceed what the campaign still has room for.
  const maxAmount = pricePerShare * remainingShares;

  const { handlingFee, totalPrice, sharesToBuy } = useMemo(
    () => derivePurchase({ amountUsd, pricePerShare, isCrowdFunding }),
    [amountUsd, pricePerShare, isCrowdFunding]
  );

  console.log("isSubmissionPending: ", isSubmissionPending);

  // Determine if the purchase button should be shown
  const showPurchaseButton =
    (!isSubmissionOwner() || currentUserUid === null) &&
    areSharesAvailable &&
    !isGoalReached;

  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!user) {
      setSnackbarMessage("You need to be logged in to comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!newComment.trim()) return;

    try {
      await addComment(submissionId, newComment, user);
      setNewComment("");
    } catch (error) {
      setSnackbarMessage("Failed to add comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle like action
  const handleLike = async () => {
    if (!user) {
      setSnackbarMessage("You need to be signed in to like this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Determine the new like status
    const newStatus = likeStatus === "like" ? null : "like";

    const response = await handleVote(submissionId, user.uid, newStatus);
    if (response.success) {
      // Update the like and dislike counts
      if (newStatus === "like") {
        setLikeCount(likeCount + 1);
        if (likeStatus === "dislike") setDislikeCount(dislikeCount - 1);
      } else if (likeStatus === "like") {
        setLikeCount(likeCount - 1);
      }
      setLikeStatus(newStatus);
    } else {
      setSnackbarMessage("Failed to like this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle dislike action
  const handleDislike = async () => {
    if (!user) {
      setSnackbarMessage(
        "You need to be signed in to dislike this submission."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Determine the new dislike status
    const newStatus = likeStatus === "dislike" ? null : "dislike";

    const response = await handleVote(submissionId, user.uid, newStatus);
    if (response.success) {
      // Update the like and dislike counts
      if (newStatus === "dislike") {
        setDislikeCount(dislikeCount + 1);
        if (likeStatus === "like") setLikeCount(likeCount - 1);
      } else if (likeStatus === "dislike") {
        setDislikeCount(dislikeCount - 1);
      }
      setLikeStatus(newStatus);
    } else {
      setSnackbarMessage("Failed to dislike this submission.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle purchase action
  const handlePurchase = async () => {
    if (!user) {
      setSnackbarMessage(
        "You need to be signed in to purchase this submission."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Unreachable through the UI now that the amount is always seeded; this
    // guards against a malformed sale record with no usable price.
    if (!(amountUsd > 0) || !(sharesToBuy > 0)) {
      setSnackbarMessage("This item is not available for purchase right now.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      // Retrieve the auth token from localStorage
      const authResponseData = JSON.parse(
        localStorage.getItem("authResponseData")
      );
      const token = authResponseData?.token;

      // Ensure the user is authenticated
      if (!token) {
        setSnackbarMessage("You must be signed in to initiate a purchase.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      console.log("submissionDetails paypal: ", submissionDetails);

      const baseKbeanURL = process.env.REACT_APP_PAYPAL_BASE_KBEAN_URL;

      console.log("baseKbeanURL: ", baseKbeanURL);

      // Prepare purchase data
      const purchaseData = {
        ArtworkId: submissionDetails.submissionSaleId,
        ArtSaleId: submissionDetails.saleId,
        Shares: sharesToBuy,
        PricePerShare: pricePerShare,
        TotalAmount: totalPrice,
        HandlingFee: handlingFee,
        SuperCoinsUsed: "",
        ClientSuccessUrl: `${baseKbeanURL}/challenge/submission/contributions`,
        ClientCancelUrl: `${baseKbeanURL}/challenge/submission/${submissionId}?transaction_error`,
      };

      console.log("purchaseData paypal: ", purchaseData);

      // Initiate the purchase
      const response = await initiatePurchase(purchaseData);
      if (response) {
        console.log("Purchase initiation response:", response);
        window.location.href = response; // Redirect user to the provided response URL
      }
    } catch (error) {
      // Handle any errors that occur during the purchase initiation
      console.error("Error initiating purchase:", error);
      setSnackbarMessage("Failed to initiate purchase. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's above other elements
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Slightly opaque black
        }}
        open={true} // Always open when in this loading state
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" color={theme.colors.red}>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Link and Title Section */}
      <SubmissionTitleSection
        submissionDetails={submissionDetails}
        theme={theme}
      />

      {/* Submission Details Section */}
      <SubmissionDetailsSection
        submissionDetails={submissionDetails}
        theme={theme}
      />

      {submissionDetails.contentType.startsWith("video/") ? (
        // Video Player Section
        <VideoPlayerSection
          submission={submissionDetails}
          onAssetStatusChange={handleAssetStatusChange}
        />
      ) : submissionDetails.contentType.startsWith("image/") ? (
        // Image Display Section
        <ImageDisplaySection
          submission={submissionDetails}
          onAssetStatusChange={handleAssetStatusChange}
        />
      ) : (
        // Fallback for unsupported content types
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            marginTop: "10px",
            fontSize: "0.9em",
            fontFamily: "basic-sans",
          }}
        >
          Unsupported content type: {submissionDetails.contentType}
        </Typography>
      )}

      {/* Like/Dislike/Purchase Section */}
      {!isProcessing && (
        <Box sx={{ mb: 4 }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Like/Dislike Section */}
            <Box>
              <Grid container spacing={2} alignItems="center">
                <LikeDislikeButtons
                  likeCount={likeCount}
                  dislikeCount={dislikeCount}
                  likeStatus={likeStatus}
                  handleLike={handleLike}
                  handleDislike={handleDislike}
                  theme={theme}
                />
              </Grid>
            </Box>

            {/* Goal Reached Message, Purchase Option, or Submission Pending */}
            {amountUsd > 0 &&
            <PurchaseSection
              submissionDetails={submissionDetails}
              isCrowdFunding={isCrowdFunding}
              isSubmissionPending={isSubmissionPending}
              isGoalReached={isGoalReached}
              showPurchaseButton={showPurchaseButton}
              amountUsd={amountUsd}
              handlingFee={handlingFee}
              maxAmount={maxAmount}
              onAmountChange={setAmountUsd}
              onPurchase={handlePurchase}
              totalShares={totalShares}
              remainingShares={remainingShares}
              pricePerShare={pricePerShare}
              theme={theme}
            />
          }
          </Grid>
        </Box>
      )}

      {/* Comments Section */}
      {!isProcessing && (
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="body1"
            sx={{ mb: 2, fontFamily: "basic-sans", fontWeight: "700", ml: 3 }}
          >
            Comments on this Submission
          </Typography>

          <CommentInput
            newComment={newComment}
            setNewComment={setNewComment}
            handleCommentSubmit={handleCommentSubmit}
          />

          <CommentsList
            comments={comments}
            loadingComments={loadingComments}
            theme={theme}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SubmissionDetailPage;
